import {
  Button,
  Checkbox,
  Chip,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import './LineItemReview.css'
import { editingLineItemType, portalLineItemType, shipToType } from './orderTypes'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import ActionBar from './ActionBar'
import {
  bulkApplyShippingAddress,
  flattenLineItems,
  lineKey,
  shipTosEqual,
  stripEditingFields,
  toShipTo,
  unflattenLineItems,
} from './editLinesHelper'

const COLUMN_WIDTHS = {
  Number: '125px',
  Supplier: '250px',
  DisplayPartNumber: '175px',
  Manufacturer: '250px',
  MfrPartNumber: '175px',
  SupplierPartNumber: '250px',
  Description: '250px',
  ShipToAddress: '250px',
  Qty: '150px',
  UnitCost: '150px',
  ExtCost: '150px',
  UnitPrice: '150px',
  ExtPrice: '150px',
}

function formatMoney(value: number | null | undefined) {
  return value?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) ?? '-'
}

function formatAddress(address: shipToType | portalLineItemType): string {
  const shipToLine = [
    address.ShipToAttention,
    address.ShipToCompany,
    address.ShipToAddress1,
    address.ShipToAddress2,
    [address.ShipToAddressCity, address.ShipToAddressState, address.ShipToAddressZip].filter(Boolean).join(', '),
    address.ShipToAddressCountry,
  ]
    .filter(Boolean)
    .join(', ')

  const receiverLine = [address.ReceiverContactName, address.ReceiverContactEmail, address.ReceiverContactPhone]
    .filter(Boolean)
    .join(' ')

  return [shipToLine, receiverLine].filter(Boolean).join('\n')
}

export default function LineItemReview({
  lineItems,
  submitLoading,
  shippingAddresses,
  isCorrection = false,
  setLineItems,
  setView,
  submitOrder,
}: {
  lineItems: portalLineItemType[]
  submitLoading: boolean
  shippingAddresses: (shipToType & { default: boolean; id: number })[]
  isCorrection?: boolean
  setLineItems: React.Dispatch<React.SetStateAction<portalLineItemType[]>>
  setView: (view: number) => void
  submitOrder: () => void
}) {
  const flatLineItems = useMemo(() => flattenLineItems(lineItems), [lineItems])

  const lastCheckedIndexRef = useRef<number | null>(null)
  const skipSyncFromPropsRef = useRef(false)

  const [editingLineItems, setEditingLineItems] = useState<editingLineItemType[]>(() =>
    flatLineItems.map(item => ({ ...item, addressId: null as number | null, selected: false })),
  )
  const [kitCollapsed, setKitCollapsed] = useState<string[]>([])
  const [activeShipToKey, setActiveShipToKey] = useState<string | null>(null)

  const persistToParent = (next: editingLineItemType[]) => {
    skipSyncFromPropsRef.current = true
    setLineItems(unflattenLineItems(stripEditingFields(next)))
  }

  useEffect(() => {
    if (skipSyncFromPropsRef.current) {
      skipSyncFromPropsRef.current = false
      return
    }
    setEditingLineItems(flatLineItems.map(item => ({ ...item, addressId: null, selected: false })))
  }, [flatLineItems])

  const defaultAddress = useMemo(() => shippingAddresses.find(address => address.default), [shippingAddresses])

  const shipToDisplay = (line: editingLineItemType) => {
    if (line.ShipToAddress1) return formatAddress(line)
    if (defaultAddress) return formatAddress(defaultAddress)
    return '-'
  }

  useEffect(() => {
    window.scrollTo(0, 0)
    if (!isCorrection) return

    const next = editingLineItems.map(item => {
      const hasShipTo = !!(item.ShipToAddress1 || item.ShipToCompany || item.ShipToAttention)
      if (!hasShipTo || shippingAddresses.some(address => shipTosEqual(address, item))) return item
      return { ...item, ...toShipTo({}), addressId: null }
    })
    if (next.some((item, i) => item !== editingLineItems[i])) {
      setEditingLineItems(next)
      persistToParent(next)
    }
  }, [])

  const applyShipTo = (line: editingLineItemType, addressId: number) => {
    const address = shippingAddresses.find(a => a.id === addressId)
    if (!address) return
    setEditingLineItems(prev => {
      const next = prev.map(item =>
        lineKey(item) === lineKey(line)
          ? {
              ...item,
              ShipToAttention: address.ShipToAttention,
              ShipToCompany: address.ShipToCompany,
              ShipToAddress1: address.ShipToAddress1,
              ShipToAddress2: address.ShipToAddress2,
              ShipToAddressCity: address.ShipToAddressCity,
              ShipToAddressState: address.ShipToAddressState,
              ShipToAddressZip: address.ShipToAddressZip,
              ShipToAddressCountry: address.ShipToAddressCountry,
              ReceiverContactName: address.ReceiverContactName,
              ReceiverContactEmail: address.ReceiverContactEmail,
              ReceiverContactPhone: address.ReceiverContactPhone,
              addressId: address.id,
            }
          : item,
      )
      persistToParent(next)
      return next
    })
  }

  const handleBulkEditShippingAddress = (addressId: number) => {
    const address = shippingAddresses.find(a => a.id === addressId)
    if (!address) return
    setEditingLineItems(prev => {
      const next = bulkApplyShippingAddress(prev, {
        id: address.id,
        ShipToAttention: address.ShipToAttention,
        ShipToCompany: address.ShipToCompany,
        ShipToAddress1: address.ShipToAddress1,
        ShipToAddress2: address.ShipToAddress2,
        ShipToAddressCity: address.ShipToAddressCity,
        ShipToAddressState: address.ShipToAddressState,
        ShipToAddressZip: address.ShipToAddressZip,
        ShipToAddressCountry: address.ShipToAddressCountry,
        ReceiverContactName: address.ReceiverContactName,
        ReceiverContactEmail: address.ReceiverContactEmail,
        ReceiverContactPhone: address.ReceiverContactPhone,
      })
      persistToParent(next)
      return next
    })
  }

  return (
    <div className="line-item-review">
      <div className="line-item-review-buttons">
        <div className="line-item-review-buttons-left">
          <Button variant="contained" onClick={() => setView(1)}>
            Previous
          </Button>
        </div>
        <div className="line-item-review-buttons-right">
          <Button
            variant="contained"
            onClick={submitOrder}
            loading={submitLoading}
            disabled={editingLineItems.length === 0}
          >
            {isCorrection ? 'Resubmit Order' : 'Create Order'}
          </Button>
        </div>
      </div>
      <TableContainer className="line-item-review-table">
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell width={COLUMN_WIDTHS.Number} className="line-item-review-number-column">
                <Checkbox
                  checked={editingLineItems.length > 0 && editingLineItems.every(item => item.selected)}
                  indeterminate={
                    editingLineItems.some(item => item.selected) && !editingLineItems.every(item => item.selected)
                  }
                  onChange={e =>
                    setEditingLineItems(prev => prev.map(item => ({ ...item, selected: e.target.checked })))
                  }
                />
                #
              </TableCell>
              <TableCell width={COLUMN_WIDTHS.Supplier}>Supplier</TableCell>
              <TableCell width={COLUMN_WIDTHS.DisplayPartNumber}>Display Part #</TableCell>
              <TableCell width={COLUMN_WIDTHS.Manufacturer}>Manufacturer</TableCell>
              <TableCell width={COLUMN_WIDTHS.MfrPartNumber}>Mfr Part #</TableCell>
              <TableCell width={COLUMN_WIDTHS.SupplierPartNumber}>Supplier Part #</TableCell>
              <TableCell width={COLUMN_WIDTHS.Description}>Description</TableCell>
              <TableCell width={COLUMN_WIDTHS.ShipToAddress}>Ship to Address</TableCell>
              <TableCell width={COLUMN_WIDTHS.Qty}>Qty</TableCell>
              <TableCell width={COLUMN_WIDTHS.UnitCost}>Unit Cost</TableCell>
              <TableCell width={COLUMN_WIDTHS.ExtCost}>Ext Cost</TableCell>
              <TableCell width={COLUMN_WIDTHS.UnitPrice}>Unit Price</TableCell>
              <TableCell width={COLUMN_WIDTHS.ExtPrice}>Ext Price</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {editingLineItems.map((lineItem, rowIndex) => (
              <TableRow
                key={`${rowIndex}-${lineKey(lineItem)}`}
                className={
                  lineItem.Number === -1
                    ? 'kit-row'
                    : lineItem.Number ===
                      Math.max(
                        ...editingLineItems.filter(item => item.Kit === lineItem.Kit).map(item => item.Number ?? 0),
                      )
                    ? 'last-bundle-child'
                    : 'line-item-review-table-row'
                }
                sx={{
                  display: lineItem.Number !== -1 && kitCollapsed.includes(lineItem.Kit ?? '') ? 'none' : 'table-row',
                }}
              >
                {lineItem.Number !== -1 ? (
                  <TableCell className="line-item-review-number-column">
                    <div className="line-item-review-number-cell-inner">
                      <Checkbox
                        checked={lineItem.selected}
                        onChange={e => {
                          const checked = e.target.checked
                          if ((e.nativeEvent as MouseEvent).shiftKey && lastCheckedIndexRef.current !== null) {
                            const from = Math.min(lastCheckedIndexRef.current, rowIndex)
                            const to = Math.max(lastCheckedIndexRef.current, rowIndex)
                            setEditingLineItems(prev =>
                              prev.map((item, i) =>
                                i >= from && i <= to && item.Number !== -1 ? { ...item, selected: checked } : item,
                              ),
                            )
                          } else {
                            setEditingLineItems(prev =>
                              prev.map(item =>
                                lineKey(item) === lineKey(lineItem) ? { ...item, selected: checked } : item,
                              ),
                            )
                          }
                          lastCheckedIndexRef.current = rowIndex
                        }}
                      />
                      <span className="line-item-review-number-text">
                        {lineItem.Number}
                        {lineItem.Bundle != null && lineItem.Bundle && (
                          <Chip label="Bundle" color="primary" variant="filled" size="small" />
                        )}
                      </span>
                    </div>
                  </TableCell>
                ) : (
                  <TableCell>
                    <Checkbox
                      checked={editingLineItems
                        .filter(item => item.Kit === lineItem.Kit && item.Number !== -1)
                        .every(item => item.selected)}
                      indeterminate={
                        editingLineItems
                          .filter(item => item.Kit === lineItem.Kit && item.Number !== -1)
                          .some(item => item.selected) &&
                        !editingLineItems
                          .filter(item => item.Kit === lineItem.Kit && item.Number !== -1)
                          .every(item => item.selected)
                      }
                      onChange={e =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            item.Kit === lineItem.Kit && item.Number !== -1
                              ? { ...item, selected: e.target.checked }
                              : item,
                          ),
                        )
                      }
                    />
                    <IconButton
                      onClick={() =>
                        setKitCollapsed(prev =>
                          prev.includes(lineItem.Kit ?? '')
                            ? prev.filter(kit => kit !== lineItem.Kit)
                            : [...prev, lineItem.Kit ?? ''],
                        )
                      }
                    >
                      {kitCollapsed.includes(lineItem.Kit ?? '') ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                    <Chip label={lineItem.Bundle ? 'Bundle' : 'Kit'} color="primary" variant="filled" size="small" />
                  </TableCell>
                )}
                <TableCell>{lineItem.Supplier ?? '-'}</TableCell>
                <TableCell>{lineItem.DisplayPartNumber ?? '-'}</TableCell>
                <TableCell>{lineItem.Manufacturer ?? '-'}</TableCell>
                <TableCell>{lineItem.MfrPartNum ?? '-'}</TableCell>
                <TableCell>{lineItem.SupplierPartNum ?? '-'}</TableCell>
                <TableCell>{lineItem.ProductDescription ?? '-'}</TableCell>
                <TableCell>
                  {lineItem.Number === -1 ? (
                    '-'
                  ) : activeShipToKey === lineKey(lineItem) ? (
                    <Select
                      className="line-item-review-active-cell"
                      size="small"
                      autoFocus
                      open
                      value={lineItem.addressId ?? defaultAddress?.id ?? ''}
                      onChange={e => {
                        applyShipTo(lineItem, Number(e.target.value))
                        setActiveShipToKey(null)
                      }}
                      onClose={() => setActiveShipToKey(null)}
                      MenuProps={{
                        PaperProps: {
                          className: 'line-item-review-ship-to-menu',
                        },
                      }}
                    >
                      {shippingAddresses.map(address => (
                        <MenuItem key={address.id} value={address.id}>
                          {formatAddress(address)}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : (
                    <span
                      className="line-item-review-editable-cell"
                      onClick={e => {
                        e.stopPropagation()
                        setActiveShipToKey(lineKey(lineItem))
                      }}
                    >
                      {shipToDisplay(lineItem)}
                    </span>
                  )}
                </TableCell>
                <TableCell>{lineItem.Quantity ?? '-'}</TableCell>
                <TableCell>{formatMoney(lineItem.UnitCost)}</TableCell>
                <TableCell>{formatMoney(lineItem.ExtCost)}</TableCell>
                <TableCell>{formatMoney(lineItem.UnitPrice)}</TableCell>
                <TableCell>{formatMoney(lineItem.ExtPrice)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ActionBar
        totalCount={editingLineItems.length}
        selectedCount={editingLineItems.filter(item => item.selected).length}
        onBulkEditShippingAddress={handleBulkEditShippingAddress}
        shippingAddresses={shippingAddresses}
      />
    </div>
  )
}
